import WRAP from "WRAP-JS";
import { MAP_ID_COPILOT } from "../../../store/MapContentsState";
import { configAircraftLayer, configCoPilotCurrentDh, configTrackLineLayer, configTrackPointLayer, setConfigAircraftLayerAttribute } from "./config/ConfigCopilot";
import {
  convertWRAPPoint,
  deepCopy,
  formatLatDM,
  formatLonDM,
  formatLonLatATC1,
  formatTimeZoneDate,
  isEmpty,
  m2d,
  makeRoundedRectangle,
  normalizeDegree,
  parseUTCDate,
  rad2deg,
  translation,
  _debounce, isEqualObject,
} from '../../../utils/FPEUtils';
import LegendCopilot from "../legend/LegendCopilot";
import { COMMUNICATE_ERROR_MESSAGE_FLIGHT, COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER, ERROR_MESSAGE_TRY_AGAIN_LATER_FLIGHT_413 } from "../../../constants/ErrorMessage";
import DeferredPromise from "../../../utils/DeferredPromise";
import { lineString} from "@turf/turf";


export const LATEST_POSITION_ONLY = '0';
export const INTERVAL_HOUR_60min = '1';
export const INTERVAL_HOUR_120min = '2';
export const INTERVAL_HOUR_180min = '3';
export const INTERVAL_HOUR_240min = '4';
export const INTERVAL_HOUR_300min = '5';
export const INTERVAL_HOUR_360min = '6';
export const INTERVAL_HOUR_420min = '7';
export const INTERVAL_HOUR_480min = '8';

class CoPilot {
  static NAME = MAP_ID_COPILOT;
  AIRCRAFT = `${MAP_ID_COPILOT}_AIRCRAFT`;
  TRACK_POINT = `${MAP_ID_COPILOT}_TRACK_POINT`;
  TRACK_LINE = `${MAP_ID_COPILOT}_TRACK_LINE`;
  ROUTE_HIGHLIGHT = `${MAP_ID_COPILOT}_ROUTE_HIGHLIGHT`;

  visible;
  userGroup;
  targetCodes;
  orgData = {};
  ready = false;
  initDataPromise;

  coPilotDh;
  aircraftLayer;
  labelLayer;
  trackPointLayer;
  trackLineLayer;
  routeHighlightLayer;

  LABEL_OFFSET_X = 15;
  LABEL_OFFSET_Y = 15;
  LABEL_PADDING = 5;

  labelFontSize = 30;
  heliLabelTextColor = '#FFFFFF';
  heliLabelBGColor = 'rgba(0, 0, 255, 0.7)';
  cessnaLabelTextColor = '#FFFFFF';
  cessnaLabelBGColor = 'rgba(0, 0, 255, 0.7)';
  naviLabelTextColor = '#FFFFFF';
  naviLabelBGColor = 'rgba(0, 0, 255, 0.7)';
  otherCompanyLabelTextColor = '#FFFFFF';
  otherCompanyLabelBGColor = 'rgba(0, 0, 255, 0.7)';
  otherLabelTextColor = '#FFFFFF';
  otherLabelBGColor = 'rgba(0, 0, 255, 0.7)';

  viewNickname = false;

  mapPerspectiveDispatcher;
  alerts = [];
  coPiMessageData = [];
  coPiMessages = [];
  errorMessageInfo;
  setCoPiLoadingState
  DISPLAY_TIME_THRESHOLD = 5;

  sources;
  tooltipSetting;
  linkableSourceList;
  NO_RECORD = '---';
  original_update;
  original_set;
  isLoadFailed = false;

  // 間引き(simplify関連)(S)===========
  // 間引かれなかった地点リスト(key: aircraft_code, value: {key: data_source_id, value: [position_id,,,]})
  simplifiedPositionId = {};
  // 前回、間引きを行った際の間引き閾値。zoom変更時にこれと比較して閾値が変わってなければ間引きを実行しない
  simplifiedThreshold
  // ===========間引き(simplify関連)(E)

  constructor() {
    this.aircraftLayer = new WRAP.Geo.Layer(this.AIRCRAFT);
    this.aircraftLayer.setVisible(false);

    this.labelLayer = new WRAP.Geo.Layer("LABEL_LAYER");
    this.labelLayer.disableInteraction(true);
    this.labelLayer.setVisible(false);

    this.trackPointLayer = new WRAP.Geo.Layer(this.TRACK_POINT);
    this.trackPointLayer.setVisible(false);

    this.trackLineLayer = new WRAP.Geo.Layer(this.TRACK_LINE);
    this.trackLineLayer.setVisible(false);

    this.routeHighlightLayer = new WRAP.Geo.Layer(this.ROUTE_HIGHLIGHT);
    this.routeHighlightLayer.disableInteraction(true);
    this.routeHighlightLayer.setVisible(false);

    this.setIntervalHour(INTERVAL_HOUR_60min);
  }

  setContentsDispatcher(dispatcher) {
    this.contentsDispatcher = dispatcher;
  }

  setMapPerspectiveDispatcher(dispatcher) {
    this.mapPerspectiveDispatcher = dispatcher;
  }

  setCoPilotDataDispatcher(dispatcher) {
    this.coPilotDataDispatcher = dispatcher;
  }

  setCoPilotDetailTargetDispatcher(dispatcher) {
    this.coPilotDetailTargetDispatcher = dispatcher;
  }

  setHighlightPointsDispatcher(dispatcher) {
    this.highlightPointsDispatcher = dispatcher;
  }

  setCoPiLoadingStateDispatcher(dispatcher) {
    this.setCoPiLoadingState = dispatcher;
  }

  async initialize() {
    await this._initData();
    this._initLayer();
    this.ready = true;
  }

  _updateDataset(trackingAircraft = this.trackingAircraft, intervalHour = this.intervalHour) {
    if (trackingAircraft !== undefined && intervalHour !== undefined) {
      var opt = {
        user_group_id: this.userGroup,
        tracking_aircraft: trackingAircraft, // API側で参照しなくなったためAPIには指定しないようする（configCoPilotCurrentDhのurlからパラメータ削除）が、loadAtManualのコールバックの中でoption.tracking_aircraftを参照するのでここは消さない
        interval_hour: intervalHour === LATEST_POSITION_ONLY ? INTERVAL_HOUR_60min : intervalHour,
      }
      Object.assign(this.coPilotDh.source.data.opt, opt);
    }
  }

  _initData() {

    this.coPilotDh = new WRAP.Data(configCoPilotCurrentDh);

    /**
     * Flight Watchの位置情報の取得に失敗した際に、retained.dataにnullが設定されてしまう。
     * 位置情報取得失敗時でも航跡を表示、かつON/OFFを行いたいためretained.dataにnullが設定されないようにWRAP.Data._updateをハックする。
     */
    this.original_set = this.coPilotDh.source.data._set;
    this.coPilotDh.source.data._set = (element, index, value) => {
      if (element === 'retained' && value === null) return;
      this.original_set(element, index, value);
    }
    this.original_update = this.coPilotDh._update;
    this.coPilotDh._update = (id, state) => {
      if (state.retained === null) {
        this.coPilotDh.status[id] = state.status;
        this.coPilotDh.revision[id] = state.revision;
      } else {
        this.original_update(id, state);
      }
    }
    this._updateDataset();

    this.coPilotDh.updated('data', (ref, _urls, option) => {
      this.isLoadFailed = false;
      if (this.errorMessageInfo) {
        this.contentsDispatcher({type: 'CLEAR_ERROR', payload: this.errorMessageInfo});
        this.errorMessageInfo = null;
      }

      if(option.loadAtManual) {
        // loadAtManualがtrueのときは差分更新なのでMerge

        const filterTarget = new Set(option.tracking_aircraft.split(","));
        // 既にデータがある場合は、更新対象のデータを削除しておく
        if (this.orgData.features) {
          this.orgData.features = this.orgData.features.filter(f => !filterTarget.has(f.properties.aircraft_code));
        }
        // 更新データを指定した機体に絞り込む
        const newData = ref.features ? ref.features.filter(f => filterTarget.has(f.properties.aircraft_code)) : [];

        this.orgData.features = [...this.orgData.features ? this.orgData.features : [], ...deepCopy(newData)];
      } else {
        // 全更新時は置き換え
        this.orgData = deepCopy(ref);
      }

      this._filterTarget();
      this._addProperties();
      this.updateCoPiMessage();
      this.coPilotDataDispatcher(this.coPilotDh.retained.data);

    }, (_, __, status) => {
      this.isLoadFailed = true;
      const is413 = status === "error:status[413]" || status === 413;
      this.errorMessageInfo = this.contentsDispatcher({
        type: 'SHOW_ERROR',payload:{message: COMMUNICATE_ERROR_MESSAGE_FLIGHT, additionalMessage: is413 ? ERROR_MESSAGE_TRY_AGAIN_LATER_FLIGHT_413 : COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
        }
      });
    });

    this.initDataPromise = new DeferredPromise();
    return this.initDataPromise.promise;
  }

  _initLayer() {
    this.aircraftLayer.configure(this.coPilotDh, configAircraftLayer);
    this.aircraftLayer.updated(this._addLabel.bind(this));

    this.trackPointLayer.configure(this.coPilotDh, configTrackPointLayer);
    this.trackPointLayer.setTooltip((geo, data) => {
      return this._trackPointTooltip(geo, data);
    });
    // ルートが簡素化によって間引かれている場合、間引かれた地点をフィルタリングする
    this.trackPointLayer.setAttributes({
      filter: (feature) => {
        //  position_idが簡素化されたものの中に含まれるか判定。
        const target = this.simplifiedPositionId[feature.properties.aircraft_code]?.[feature.properties.data_source_id];
        if (target) {
          return target.has(feature.properties.position_id);
        }
        return true;
      },
    });

    this.trackLineLayer.configure(this.coPilotDh, configTrackLineLayer);

    WRAP.Geo.addEventHandler('touch', this._pointTouchHandler.bind(this));
    WRAP.Geo.addEventHandler('mouseover', this._trackMouseOverHandler.bind(this));
    WRAP.Geo.addEventHandler('mouseout', this._trackMouseOutHandler.bind(this));
  }

  _pointTouchHandler(layer, feature) {
    if (layer !== this.aircraftLayer) return;
    this.coPilotDetailTargetDispatcher(feature.geo.properties.aircraft_code)
  }

  _trackMouseOverHandler(layer, feature) {
    if (layer !== this.trackLineLayer && layer !== this.trackPointLayer) return;

    let target;
    if (layer._name === this.TRACK_POINT) {
      target = [
        {
          source: 'horizontal',
          type: 'fix',
          name: feature.geo.properties.name,
          lon: feature.geo.geometry.coordinates[0],
          lat: feature.geo.geometry.coordinates[1],
          index: 0
        }
      ];
    } else {
      target = [
        {
          source: 'horizontal',
          type: 'route',
          name: feature.geo.properties.names[0],
          lon: feature.geo.geometry.coordinates[0][0],
          lat: feature.geo.geometry.coordinates[0][1],
          index: feature.geo.properties.lineIndex
        },
        {
          source: 'horizontal',
          type: 'route',
          name: feature.geo.properties.names[1],
          lon: feature.geo.geometry.coordinates[1][0],
          lat: feature.geo.geometry.coordinates[1][1],
          index: feature.geo.properties.lineIndex + 1
        }
      ];
    }
    this.highlightPointsDispatcher(target);
  }

  _trackMouseOutHandler(layer) {
    if (layer !== this.trackLineLayer && layer !== this.trackPointLayer) return;
    this.highlightPointsDispatcher([]);
  }

  async drawLayer(visible) {
    this.visible = visible;
    if (!this.ready) {
      await this.initialize();
    }
    this.setVisible(visible);
  }

  getLayer() {
    return this.getAllLayers();
  }

  getAllLayers() {
    return [this.trackLineLayer, this.trackPointLayer, this.labelLayer, this.aircraftLayer, this.routeHighlightLayer];
  }

  setVisible(visible) {
    if (visible) {
      this.aircraftLayer.show();
      this.labelLayer.show();
      this._changeTrackLayerVisible();
      this.updateCoPiMessage();
      this.startMonitoringMessageExpiration();
    } else {
      this.aircraftLayer.hide();
      this.labelLayer.hide();
      this.trackPointLayer.hide();
      this.trackLineLayer.hide();
      this.routeHighlightLayer.hide();
      this.removeCoPiMessage();
      this.endMonitoringMessageExpiration();
    }
    this.coPilotDh.setAutoUpdate('data', visible);
    this.setCoPiLoadingState(visible);
  }

  _changeTrackLayerVisible() {
    if (this.isViewTrack && this.visible) {
      this.trackPointLayer.show();
      this.trackLineLayer.show();
      this.routeHighlightLayer.show();
    } else {
      this.trackPointLayer.hide();
      this.trackLineLayer.hide();
      this.routeHighlightLayer.hide();
    }
  }

  _batteryValue2Str(code) {
    let ret = '-';
    if (code <= 3) {
      ret = 'Empty'
    } else if (code <= 3.5) {
      ret = 'Good';
    } else if (code < 4.8) {
      ret = 'Full';
    }
    return ret;
  }

  _trackPointTooltip(geo, data) {
    const prop = geo.properties;
    const coordinates = geo.geometry.coordinates;
    if (!prop) return;

    const alertList = [];
    if (prop.approach_alert) alertList.push(translation('approach'));
    if (prop.deviation_alert) alertList.push(translation('deviation'));
    const alertText = alertList.join(', ');

    const source = this.linkableSourceList.find(l => l.data_source_id === prop.data_source_id)?.data_source_name;
    const tooltipData = {
      "%conv_obs_time%": prop.obs_time ? formatTimeZoneDate(prop.obs_time) : this.NO_RECORD,
      "%conv_alt_ft%": typeof prop.alt_ft === "number" ? Math.round(prop.alt_ft * 100) / 100 : this.NO_RECORD,
      "%conv_lat%": coordinates[1] ? formatLatDM(coordinates[1]) : this.NO_RECORD,
      "%conv_lon%": coordinates[0] ? formatLonDM(coordinates[0]) : this.NO_RECORD,
      "%conv_battery%": typeof prop.battery === "number" ? this._batteryValue2Str(prop.battery) : this.NO_RECORD,
      "%conv_alert%": alertText ? alertText : this.NO_RECORD,
      "%conv_source%": source ? source : this.NO_RECORD
    }

    const tooltips = this.tooltipSettings[geo.properties.data_source_id]?.["CoPilot-tooltip"].map(tooltip => {
      const keys = tooltip.value.match(/%\w+%/g);
      let value = tooltip.value;
      keys.forEach(key => {
        const convValue = tooltipData[key];
        if (convValue !== undefined) {
          value = value.replace(key, convValue);
        } else {
          const rowValue = prop[key.replace(/%/g, "")];
          value = value.replace(key, rowValue !== null && rowValue !== undefined ? rowValue : this.NO_RECORD);
        }
      });
      return {...tooltip, ...{value: value}};
    });

    let tooltip = `<div>${prop.aircraft_code} (${prop.aircraft_name})</div>`;
    tooltips?.forEach(t => {
      tooltip += `<div class="coPi-tooltip-item">
        <div class="coPi-tooltip-title">${t.label}</div>
        <div class="coPi-tooltip-sep">:</div>
        <div>${t.value}</div>
      </div>`
    });

    return tooltip;
  }

  // 表示対象のUserGroupを設定する
  setUserGroup(group) {
    this.userGroup = group;
  }

  // 表示対象のaircraft_codeを設定する
  setTargetCode(targetCodes) {
    const currentTargets = this.targetCodes;
    this.targetCodes = targetCodes;
    if (!this.ready) {
      if (this.initDataPromise?.state === 'pending') {
        this.coPilotDh.source.data.conf.auto_update = true;
        this.initDataPromise.resolve();
      }
      this.setTrackingAircraft(this.targetCodes);
      return;
    }
    if (currentTargets.length < targetCodes.length && !this.isLoadFailed) {
      const newTargets = targetCodes.filter(c => !currentTargets.includes(c));
      this.loadByTrackingAircraft(newTargets);
    } else {
      this._reDraw();
    }
    this.setTrackingAircraft(this.targetCodes);
  }

  getPerspective(coordinates) {
    const points = coordinates.map(coordinate => {
      return convertWRAPPoint(coordinate[1], coordinate[0]);
    });
    const perspective = WRAP.Geo.getPerspective(points, 85);

    return {
      centerLat: m2d(perspective.center.lat),
      centerLon: m2d(perspective.center.lon)
    }
  }

  getIntervalHour() {
    return this.intervalHour;
  }

  setIntervalHour(intervalHour) {
    this.intervalHour = intervalHour;
    this.isViewTrack = this.intervalHour !== LATEST_POSITION_ONLY;
    this._changeTrackLayerVisible();
    this._updateDataset();
    if (!this.ready) return;
    this.coPilotDh.load('data', null, true);
  }

  // 表示設定を反映する
  setDisplaySettings(settings) {
    // iconへ設定反映
    const attribute = setConfigAircraftLayerAttribute(settings, this.ready);

    // labelへ設定反映
    let redrawLabel = false;
    if (this.labelFontSize !== settings.labelTextSize ||
      this.labelFillStyle !== settings.labelBackgroundColor ||
      this.labelFontStyle !== settings.labelTextColor ||
      this.viewNickname !== (settings.label === 'name') ||
      this.heliLabelTextColor !== settings.heliLabelTextColor ||
      this.heliLabelBGColor !== settings.heliLabelBGColor ||
      this.cessnaLabelTextColor !== settings.cessnaLabelTextColor ||
      this.cessnaLabelBGColor !== settings.cessnaLabelBGColor ||
      this.naviLabelTextColor !== settings.naviLabelTextColor ||
      this.naviLabelBGColor !== settings.naviLabelBGColor ||
      this.otherCompanyLabelTextColor !== settings.otherCompanyLabelTextColor ||
      this.otherCompanyLabelBGColor !== settings.otherCompanyLabelBGColor ||
      this.otherLabelTextColor !== settings.otherLabelTextColor ||
      this.otherLabelBGColor !== settings.otherLabelBGColor
    ) {
      redrawLabel = true
    }

    this.labelFontSize = settings.labelTextSize;
    this.labelFillStyle = settings.labelBackgroundColor;
    this.labelFontStyle = settings.labelTextColor;
    this.viewNickname = settings.label === 'name';
    this.heliLabelTextColor = settings.heliLabelTextColor ? settings.heliLabelTextColor : this.heliLabelTextColor;
    this.heliLabelBGColor = settings.heliLabelBGColor ? settings.heliLabelBGColor : this.heliLabelBGColor;
    this.cessnaLabelTextColor = settings.cessnaLabelTextColor ? settings.cessnaLabelTextColor : this.cessnaLabelTextColor;
    this.cessnaLabelBGColor = settings.cessnaLabelBGColor ? settings.cessnaLabelBGColor : this.cessnaLabelBGColor;
    this.naviLabelTextColor = settings.naviLabelTextColor ? settings.naviLabelTextColor : this.naviLabelTextColor;
    this.naviLabelBGColor = settings.naviLabelBGColor ? settings.naviLabelBGColor : this.naviLabelBGColor;
    this.otherCompanyLabelTextColor = settings.otherCompanyLabelTextColor ? settings.otherCompanyLabelTextColor : this.otherCompanyLabelTextColor;
    this.otherCompanyLabelBGColor = settings.otherCompanyLabelBGColor ? settings.otherCompanyLabelBGColor : this.otherCompanyLabelBGColor;
    this.otherLabelTextColor = settings.otherLabelTextColor ? settings.otherLabelTextColor : this.otherLabelTextColor;
    this.otherLabelBGColor = settings.otherLabelBGColor ? settings.otherLabelBGColor : this.otherLabelBGColor;

    // アイコンサイズに応じてラベルのオフセット値を調整
    let offset = settings.iconSize / 2;
    if (offset < 10) offset = 10;
    this.LABEL_OFFSET_X = this.LABEL_OFFSET_Y = offset;

    if (!this.ready) return;

    this.aircraftLayer.setAttributes(attribute);
    if (redrawLabel) {
      this._addLabel();
    }
  }

  setAlerts(alerts) {
    this.alerts = alerts;
  }

  setMessages(coPiMessages) {
    this.coPiMessages = coPiMessages;
    if (!this.ready) return;
    this.updateCoPiMessage();
  }

  updateCoPiMessage() {
    const min = Date.now() - this.DISPLAY_TIME_THRESHOLD * 60 * 1000;

    this.coPiMessages.forEach(coPiMessage => {
      if (coPiMessage.timestamp) {
        const dateStr = formatTimeZoneDate(coPiMessage.timestamp, 'utc', 'YYYY/MM/DD HH:mm:ss');
        const time = parseUTCDate(dateStr).getTime();
        // 受信から閾値分以内のMessageの吹き出しを作る
        if (min <= time) {

          // 表示位置を取るため最新位置のfeatureを探す
          let coPiLatestPosition = null;

          if (this.coPilotDh.retained.data) {
            const targetSourceId = this.linkableSourceList[0].data_source_id; // copiのdata_source_id

            // filter中に最新のものを直接見つける
            coPiLatestPosition = this.coPilotDh.retained.data.reduce((latest, d) => {
              // 条件に合わないものはスキップ
              if (d.properties.aircraft_code !== coPiMessage.aircraft_code ||
                !d.properties.is_current_position ||
                d.properties.data_source_id !== targetSourceId) {
                return latest;
              }

              // 最新のものと比較
              if (!latest || d.properties.obs_time_epoch > latest.properties.obs_time_epoch) {
                return d;
              }
              return latest;
            }, null);
          }

          if (coPiLatestPosition !== null) {

            // 機体の描画済みの吹き出しを探す
            const drawn = this.coPiMessageData.find(d => d.aircraft_code === coPiMessage.aircraft_code);
            if (drawn) {
              // メッセージの内容、機体のポジションが変わらない場合はスルー
              if (drawn.message === coPiMessage.message && drawn.coordinates[0] === coPiLatestPosition.geometry.coordinates[0] && drawn.coordinates[1] === coPiLatestPosition.geometry.coordinates[1]) {
                drawn.show = true;
                return;
              } else {
                WRAP.Geo.removeOverlay(drawn.element);
                this.coPiMessageData　= this.coPiMessageData.filter(d => d !== drawn);
              }
            }

            // element
            const element = document.createElement('div');
            element.classList.add('message-bubbles');
            let message = coPiMessage.message;
            if (15 < message.length) {
              message = message.slice(0, 15) + '...';
            }
            element.appendChild(document.createTextNode(message));

            this.coPiMessageData.push({
              aircraft_code: coPiMessage.aircraft_code,
              element: element,
              coordinates: coPiLatestPosition.geometry.coordinates,
              time: time,
              message: coPiMessage.message,
              show: true
            });
          } else {
            // Messageはあるが、描画対象のデータが無い場合は吹き出しを消す
            const drawn = this.coPiMessageData.find(d => d.aircraft_code === coPiMessage.aircraft_code);
            if (drawn) {
              WRAP.Geo.removeOverlay(drawn.element);
              drawn.show = false;
            }
          }
        }
      }
    });

    this.coPiMessageData.forEach(data => {
      if (data.show && min <= data.time) {
        WRAP.Geo.addOverlay(
          data.element,
          convertWRAPPoint(data.coordinates[1], data.coordinates[0]),
          -15,
          -40
        );
      }
    });
  }

  removeCoPiMessage() {
    this.coPiMessageData.forEach(data => {
      WRAP.Geo.removeOverlay(data.element);
    });
    this.coPiMessageData = [];
  }

  startMonitoringMessageExpiration() {
    this.intervalId = setInterval(() => {
      const min = Date.now() - this.DISPLAY_TIME_THRESHOLD * 60 * 1000;

      // 受信時刻が閾値以上前の吹き出しを消す。
      const deleteTarget = this.coPiMessageData.filter(data => data.time < min);
      deleteTarget.forEach(data => {
        WRAP.Geo.removeOverlay(data.element);
      });
      this.coPiMessageData = this.coPiMessageData.filter(data => !deleteTarget.includes(data));
    }, 10000);
  }

  endMonitoringMessageExpiration() {
    clearInterval(this.intervalId);
  }

  setTrackingAircraft(targetCodes) {
    this.trackingAircraft = targetCodes.join(',');
    this._updateDataset();
  }

  loadByTrackingAircraft(targetCodes) {
    this.setTrackingAircraft(targetCodes);
    this.coPilotDh.load('data', {loadAtManual: true}, true);
  }

  // 対象ルートが収まる画角、ズームをmapPerspectiveStateに設定する
  setFocusTarget(focusTarget) {
    if (!this.ready) return;

    const targetPoint = this.coPilotDh.retained.data?.reduce((latest, d) => {
      const p = d.properties;

      if (
        isNaN(p.icon_id) ||
        p.aircraft_code !== focusTarget ||
        !p.is_current_position
      ) {
        return latest;
      }

      return !latest || p.obs_time_epoch > latest.properties.obs_time_epoch
        ? d
        : latest;
    }, null);

    if (!targetPoint) return;
    const warpPoint = convertWRAPPoint(targetPoint.geometry.coordinates[1], targetPoint.geometry.coordinates[0]);
    const perspective = WRAP.Geo.getPerspective([warpPoint], 85);
    this.mapPerspectiveDispatcher({
      centerLat: m2d(perspective.center.lat),
      centerLon: m2d(perspective.center.lon)
    })
  }

  // 表示対象のaircraft_codeのデータに絞る
  _filterTarget() {
    const features = this.orgData.features;
    if (!features) {
      this.coPilotDh.retained.data = features;
      return;
    }

    const targetSet = new Set(this.targetCodes);
    const hasSourceFilter = !isEmpty(this.sources);

    this.coPilotDh.retained.data = deepCopy(features.filter(f => {
      const code = f.properties.aircraft_code;

      if (!targetSet.has(code)) return false;

      if (hasSourceFilter) {
        return this.sources[code]?.[f.properties.data_source_id];
      }

      return true;
    }));

  }

  _invalidateAllLayer() {
    this.aircraftLayer.invalidate();
    this.labelLayer.invalidate();
    this.trackPointLayer.invalidate();
    this.trackLineLayer.invalidate();
  }

  // 描画に必要なプロパティを追加する
  _addProperties() {
    if (!this.coPilotDh.retained.data) return;
    // 現在時刻（epoch秒）
    const nowSec = Math.floor(Date.now() / 1000);
    // 15分前（秒）
    const thresholdSec = nowSec - 15 * 60;

    const pointsGroupByAircraftCode = {}; // latestPosition用 (key: aircraft_code, value: {latestPosition: 最新ポジション})
    const pointsGroupByAircraftAndSource = {}; // LineString用 (key: aircraft_code + data_source_id, value: {lastAccepted: 間引かれなかった直近の描画Point, lastAcceptedIdx:lastAcceptedのindex, simplifiedPositionId:間引かれなかった地点のposition_idリスト, isSimplified:間引き前後Pointの色付けフラグ})

    // zoomレベルに応じた間引き間隔
    const minIntervalSecByZoom = this._minIntervalSecByZoom();
    // simplifyを実行した際の閾値を記録しておく
    this.simplifiedThreshold = minIntervalSecByZoom;

    const pointFeatures = [];
    const lineStringFeatures = [];

    // Point（Pointの並びはobs_timeの照準でAPI側でソート済みの前提）
    this.coPilotDh.retained.data.forEach(((f, idx) => {
      if (f.geometry.type !== 'Point') {
        /*
         _addPropertiesは_filterTargetが直前に呼ばれるので、
         this.coPilotDh.retained.dataはthis.orgDataを元に作られるためPointが入ることはないが念のための予防
         */
        return;
      }
      f.properties.point = true;
      f.properties.pointSimplify = false;
      f.properties.name = formatLonLatATC1(f.geometry.coordinates[0], f.geometry.coordinates[1]);
      let time;
      if (f.properties.obs_time) {
        time = f.properties.obs_time_epoch;
      }
      pointFeatures.push(f);

      // Pointを元にLineStringの元データ作成
      const aircraftCode = f.properties.aircraft_code;
      const dataSource = f.properties.data_source_id;
      const compositeKey = `${aircraftCode}:${dataSource}`; // 複合キー

      // latestPosition用（aircraftCodeのみでグルーピング）
      const aircraftGroup = pointsGroupByAircraftCode[aircraftCode] ||
        (pointsGroupByAircraftCode[aircraftCode] = { latestPosition: null });

      // LineString用（aircraftCode + dataSourceでグルーピング）
      const tmp =
        pointsGroupByAircraftAndSource[compositeKey] ||
        (pointsGroupByAircraftAndSource[compositeKey] = {
          lastAccepted: null,
          lastAcceptedIdx: 0,
          simplifiedPositionId: new Set(),
          isSimplified: false
        });

      /*
       latestPositionの更新（aircraftCode単位）
      複数sourceではis_current_positionがそれぞれの最新データでtrueになる。
      後続の処理で最新のobs_time（latestPosition）ものにのみcurrent_position_idを設定する。
      */
      if (f.properties.is_current_position && time && thresholdSec <= time) {
        if (aircraftGroup.latestPosition === null ||
          time > aircraftGroup.latestPosition.properties.obs_time_epoch) {
          aircraftGroup.latestPosition = f;
        }
      }

      /*
        Point間引き処理。
        先頭地点は間引かない。
        最終（最新）地点は間引かない。
        断面図表示中のものは間引かない。
        間引かれなかった直近の描画Pointからの経過時間で(zoomに応じた)間引き処理。
       */
      if (tmp.lastAccepted === null) {
        // 間引かれなかった直近の描画Pointとして保持
        tmp.lastAccepted = f;
        // 間引かれなかった地点のposition_idリストに追加
        tmp.simplifiedPositionId.add(f.properties.position_id);
      } else {
        const prev = tmp.lastAccepted;

        // 最終（最新）地点は間引かない。断面図表示中のものは間引かない。
        if (!f.properties.is_current_position && f.properties.aircraft_code !== this.csTarget?.id) {
          // 間引かれなかった直近の描画Pointからの経過時間で(zoomに応じた)間引き処理
          const dt =
            f.properties.obs_time_epoch - prev.properties.obs_time_epoch;

          if (dt < minIntervalSecByZoom) {
            // 間引き前後のPointの色を変更するため、保持
            tmp.isSimplified = true;
            return;
          }
        }

        // 間引き前後の色を変更する情報を付加
        if (tmp.isSimplified) {
          prev.properties.pointSimplify = true;
          f.properties.pointSimplify = true;
          tmp.isSimplified = false;
        }

        // 間引かれなかった地点のposition_idリストに追加
        tmp.simplifiedPositionId.add(f.properties.position_id);

        // LineStringのデータ作成（グラデーション用にFIX間毎に分割する）
        const alt_ft = [prev.properties.alt_ft, f.properties.alt_ft];
        const names = [prev.properties.name, f.properties.name];

        const feature  = lineString([prev.geometry.coordinates, f.geometry.coordinates], {
          aircraft_code: f.properties.aircraft_code,
          source_id: f.properties.data_source_id,
          gradation: true,
          names,
          lineIndex: [tmp.lastAcceptedIdx, idx],
          alt_ft,
        });
        lineStringFeatures.push(feature);

        tmp.lastAccepted = f;
        tmp.lastAcceptedIdx = idx
      }
    }));

    // latestPositionにcurrent_position_idとアラート情報を設定する。
    Object.keys(pointsGroupByAircraftCode).forEach((key) => {
      const tmp = pointsGroupByAircraftCode[key];
      const f = tmp.latestPosition;
      if (f !== null) {
        if (![6, 7, 8, 9].includes(f.properties.icon_id)) {
          f.properties.current_position_id = 0;
        } else {
          f.properties.current_position_id = f.properties.icon_id;
        }
        // 最新ポジションにアラート情報を設定
        const alert = this.alerts.find(a => a.aircraft_code === f.properties.aircraft_code && a.useCopiList);
        if (alert?.alert_on) {
          f.properties.current_position_id += 999990;
          f.properties.hasError = true;
        }
      }
    });

    Object.keys(pointsGroupByAircraftAndSource).forEach((compositeKey) => {
      const tmp = pointsGroupByAircraftAndSource[compositeKey];
      const i = compositeKey.lastIndexOf(':');
      const aircraftCode = compositeKey.slice(0, i);
      const dataSource = compositeKey.slice(i + 1);
      if (!this.simplifiedPositionId[aircraftCode]) {
        this.simplifiedPositionId[aircraftCode] = {};
      }
      this.simplifiedPositionId[aircraftCode][dataSource] = tmp.simplifiedPositionId;
    });

    // アラート情報を最優先にしてソート
    pointFeatures.sort((a, b) => {
      // 最新地点のアラート優先（true を後ろに回す）
      if (a.properties.hasError !== b.properties.hasError) {
        return a.properties.hasError ? 1 : -1;
      }
      // 時刻は epoch 秒で比較
      return a.properties.obs_time_epoch - b.properties.obs_time_epoch;
    });

    // const { lineStringFeatures, simplifiedPositionId } = this._simplifyTrack(this.coPilotDh.retained.data);
    this.coPilotDh.retained.data = [...pointFeatures, ...lineStringFeatures];

  }

  _addLabel() {
    if (!this.ready || !this.coPilotDh.retained.data) return;
    this.labelLayer.clear();
    const mask = WRAP.Geo.getMask();
    mask.clear();
    if (!this.coPilotDh.retained.data.filter) {
      this._filterTarget();
    }
    const targetFeatures = this.coPilotDh.retained.data.filter(d => d.properties.is_current_position && d.properties.current_position_id !== undefined);
    targetFeatures.forEach(f => {
      let text = f.properties.aircraft_code;
      if (this.viewNickname) {
        text = f.properties.aircraft_name;
      }
      const point = [...f.geometry.coordinates];
      const [textWidth, textHeight] = WRAP.Geo.getTextMetricsAccurate(text, this.labelFontSize);
      const labelWidth = textWidth + this.LABEL_PADDING * 2;
      const labelHeight = textHeight + this.LABEL_PADDING * 2;

      const routeCoordinates = this.coPilotDh.retained.data.find(d => {
        return d.geometry.type === 'LineString' && d.properties.aircraft_code === f.properties.aircraft_code
      })?.geometry.coordinates;
      let deg = 315;
      if (routeCoordinates && routeCoordinates.length > 1) {
        const currentPos = routeCoordinates[routeCoordinates.length - 1];
        const prevPos = routeCoordinates[routeCoordinates.length - 2];
        const currentScreenPos = WRAP.Geo.getScreenPoint(convertWRAPPoint(currentPos[1], currentPos[0]));
        const prevScreenPos =  WRAP.Geo.getScreenPoint(convertWRAPPoint(prevPos[1], prevPos[0]));
        const rad = Math.atan2(prevScreenPos.y - currentScreenPos.y, prevScreenPos.x - currentScreenPos.x);
        deg = normalizeDegree(rad2deg(rad));
      }

      const offsets = [
        {
          deg: 315,
          offsetX: this.LABEL_OFFSET_X,
          offsetY: this.LABEL_OFFSET_Y,
          linePos: [this.LABEL_OFFSET_X, this.LABEL_OFFSET_Y]
        },
        {
          deg: 360,
          offsetX: -labelWidth / 2,
          offsetY: this.LABEL_OFFSET_Y,
          linePos: [0, this.LABEL_OFFSET_Y]
        },
        {
          deg: 0,
          offsetX: -labelWidth / 2,
          offsetY: this.LABEL_OFFSET_Y,
          linePos: [0, this.LABEL_OFFSET_Y]
        },
        {
          deg: 45,
          offsetX: -labelWidth - this.LABEL_OFFSET_X,
          offsetY: this.LABEL_OFFSET_Y,
          linePos: [-this.LABEL_OFFSET_X, this.LABEL_OFFSET_Y]
        },
        {
          deg: 90,
          offsetX: -labelWidth - this.LABEL_OFFSET_X,
          offsetY: -labelHeight / 2,
          linePos: [-this.LABEL_OFFSET_X, 0]
        },
        {
          deg: 135,
          offsetX: -labelWidth - this.LABEL_OFFSET_X,
          offsetY: -labelHeight -this.LABEL_OFFSET_Y,
          linePos: [-this.LABEL_OFFSET_X, -this.LABEL_OFFSET_Y]
        },
        {
          deg: 180,
          offsetX: -labelWidth / 2,
          offsetY: -labelHeight -this.LABEL_OFFSET_Y,
          linePos: [0, -this.LABEL_OFFSET_Y]
        },
        {
          deg: 225,
          offsetX: 15,
          offsetY: -labelHeight -this.LABEL_OFFSET_Y,
          linePos: [this.LABEL_OFFSET_Y, -this.LABEL_OFFSET_Y]
        },
        {
          deg: 270,
          offsetX: this.LABEL_OFFSET_X,
          offsetY: -labelHeight / 2,
          linePos: [this.LABEL_OFFSET_X, 0]
        },

      ];

      let offset = offsets.find(o => {
        return Math.abs(o.deg - deg) <= 22.5;
      });
      let labelRightPos = labelWidth + offset.offsetX;
      let labelBottomPos = labelHeight + offset.offsetY;

      if (mask.intersectRect(point, offset.offsetX, offset.offsetY, labelWidth, labelHeight)) {
        for(let i = 0; i < offsets.length; i++) {
          const tempOffset = offsets[i];
          if (!mask.intersectRect(point, tempOffset.offsetX, tempOffset.offsetY, labelWidth, labelHeight)) {
            offset = tempOffset;
            labelRightPos = labelWidth + offset.offsetX;
            labelBottomPos = labelHeight + offset.offsetY;
            break;
          }
        }
      }

      mask.addRect(point, offset.offsetX, offset.offsetY, labelWidth, labelHeight);
      let textColor, fillColor;
      switch(f.properties.current_position_id) {
        case 6:
        case 999996:
          textColor = this.heliLabelTextColor;
          fillColor = this.heliLabelBGColor;
          break;
        case 7:
        case 999997:
          textColor = this.cessnaLabelTextColor;
          fillColor = this.cessnaLabelBGColor;
          break;
        case 8:
        case 999998:
          textColor = this.naviLabelTextColor;
          fillColor = this.naviLabelBGColor;
          break;
        case 9:
        case 999999:
          textColor = this.otherCompanyLabelTextColor;
          fillColor = this.otherCompanyLabelBGColor;
          break;
        default:
          textColor = this.otherLabelTextColor;
          fillColor = this.otherLabelBGColor;
          break;
      }

      // 機種名座布団
      const aircraftCushion = new WRAP.Geo.Feature.Line({
        point: point,
        line: makeRoundedRectangle([
          // 左上
          [offset.offsetX, offset.offsetY],
          // 左下
          [offset.offsetX, labelBottomPos],
          // 右下
          [labelRightPos, labelBottomPos],
          // 右上
          [labelRightPos, offset.offsetY],
        ], 7),
        lineWidth: 1,
        strokeStyle: fillColor,
        fillStyle: fillColor
      });
      this.labelLayer.addFeature(aircraftCushion);

      // 座布団とアイコンを結ぶ線
      const connectionLine = new WRAP.Geo.Feature.Line({
        point: point,
        line: [
          [0, 0],
          offset.linePos
        ],
        lineWidth: 1.5,
        strokeStyle: '#000000',
        fillStyle: '#000000'
      });
      this.labelLayer.addFeature(connectionLine);

      // 機種名
      const aircraftLabel = new WRAP.Geo.Feature.Text({
        align: 'left',
        fillStyle: textColor,
        fontSize: this.labelFontSize,
        offsetX: offset.offsetX + this.LABEL_PADDING,
        offsetY: textHeight + offset.offsetY + this.LABEL_PADDING,
        point: point,
        text: text,
      });
      this.labelLayer.addFeature(aircraftLabel);
    });
  }

  updateBounds() {
    this._addLabel();
    this.updateSimplify();
  }

  getLegend() {
    return {
      id: CoPilot.NAME,
      label: 'Flight Watch',
      component: LegendCopilot,
      contents: this
    }
  }

  setHighlightPoints(highlightPoints) {
    this.routeHighlightLayer.clear();
    if (highlightPoints && highlightPoints.length > 0) {
      if (highlightPoints.length === 1) {
        const point = highlightPoints[0];
        if (point.source !== 'horizontal' && point.type === 'fix') {
          this.routeHighlightLayer.addFeature(new WRAP.Geo.Feature.Point({
            point: [point.lon, point.lat],
            strokeStyle: '#880',
            lineWidth: 2,
            fillStyle: '#ff7b00',
            pointSize: 14
          }));
        }
      } else {
        if (highlightPoints[0].source !== 'horizontal') {
          this.routeHighlightLayer.addFeature(new WRAP.Geo.Feature.GeoLine({
            path: highlightPoints.map(p => [p.lon, p.lat]),
            strokeStyle: 'rgba(255, 123, 0, 0.5)',
            lineWidth: 7,
            method: 'GC'
          }));
        }
      }
    }
    // }
    WRAP.Geo.invalidate();
  }

  setCoPiSources(coPiSources) {
    if (isEqualObject(this.sources, coPiSources)) return;
    this.sources = coPiSources;
    if(isEmpty(this.orgData)) return;
    this._reDraw();
  }

  setCsTarget(csTarget) {
    this.csTarget = csTarget;
    if (!this.visible || isEmpty(this.orgData?.features)) return;
    // 断面図表示中の機体はPointの間引きを行わないので間引き処理を実行
    this._simplifyTrack(this.coPilotDh.retained.data);
    // layerを更新
    this.trackPointLayer.invalidate();
    this.trackLineLayer.invalidate();
  }

  _reDraw() {
    this._filterTarget();
    this._addProperties();
    this.updateCoPiMessage();
    this._invalidateAllLayer();
  }

  setTooltipSettings(tooltipSettings) {
    this.tooltipSettings = tooltipSettings;
  }

  setLinkableSourceList(linkableSourceList) {
    this.linkableSourceList = linkableSourceList;
  }

  updateSimplify = _debounce(() => {
    if (!this.visible || isEmpty(this.orgData?.features)) return;
    const threshold = this._minIntervalSecByZoom();
    // 前回simplify実施時と閾値が変わっていなければsimplifyを行わない
    if (threshold === this.simplifiedThreshold) return;
    // 間引き閾値が変わったらsimplifyでデータを間引く
    this._simplifyTrack(this.coPilotDh.retained.data, threshold);

    // layerを更新
    this.trackPointLayer.invalidate();
    this.trackLineLayer.invalidate();
    // 500ms後に実行
  }, 500, {leading: false, trailing: true});

  _simplifyTrack(data, minIntervalSecByZoom = this._minIntervalSecByZoom()) {
    if (!this.ready || isEmpty(data)) {
      return;
    }
    const pointFeatures = [];
    // LineString(航跡)をFIX間毎に分割する
    const lineStringFeatures = [];

    // simplifyを実行した際の閾値を記録しておく
    this.simplifiedThreshold = minIntervalSecByZoom;

    const pointsGroupByAircraftAndSource = {};

    data.forEach((_f, idx) => {
      if (_f.geometry.type !== 'Point') {
        // LineStringは除外
        return;
      }
      //dataがrecoilのせいでイミュータブルになっているのでシャローコピーしておかないとpointSimplifyを更新できない
      const f = {..._f, properties: {..._f.properties, pointSimplify: false}};

      pointFeatures.push(f);

      const aircraftCode = f.properties.aircraft_code;
      const dataSource = f.properties.data_source_id;
      const compositeKey = `${aircraftCode}:${dataSource}`; // 複合キー

      // LineString用（aircraftCode + dataSourceでグルーピング）
      const tmp =
        pointsGroupByAircraftAndSource[compositeKey] ||
        (pointsGroupByAircraftAndSource[compositeKey] = {
          lastAccepted: null,
          lastAcceptedIdx: 0,
          simplifiedPositionId: new Set(),
          isSimplified: false
        });

      // LineStringのデータ作成（グラデーション用にFIX間毎に分割する）
      if (tmp.lastAccepted === null) {
        tmp.lastAccepted = f;
        tmp.simplifiedPositionId.add(f.properties.position_id);
      } else {
        const prev = tmp.lastAccepted;

        // 最終（最新）地点は間引かない。断面図表示中のものは間引かない
        if (!f.properties.is_current_position && f.properties.aircraft_code !== this.csTarget?.id) {
          // zoomに応じた間引き処理
          const dt =
            f.properties.obs_time_epoch - prev.properties.obs_time_epoch;

          if (dt < minIntervalSecByZoom) {
            // 間引き前後のPointの色を変更するため、保持
            tmp.isSimplified = true;
            return;
          }
        }

        // 間引き前後の色を変更する情報を付加
        if (tmp.isSimplified) {
          prev.properties.pointSimplify = true;
          f.properties.pointSimplify = true;
          tmp.isSimplified = false;
        }

        tmp.simplifiedPositionId.add(f.properties.position_id);

        const alt_ft = [prev.properties.alt_ft, f.properties.alt_ft];
        const names = [prev.properties.name, f.properties.name];

        const feature  = lineString([prev.geometry.coordinates, f.geometry.coordinates], {
          aircraft_code: f.properties.aircraft_code,
          source_id: f.properties.data_source_id,
          gradation: true,
          names,
          lineIndex: [tmp.lastAcceptedIdx, idx],
          alt_ft,
        });
        lineStringFeatures.push(feature);

        tmp.lastAccepted = f;
        tmp.lastAcceptedIdx = idx
      }
    });

    Object.keys(pointsGroupByAircraftAndSource).forEach((compositeKey) => {
      const tmp = pointsGroupByAircraftAndSource[compositeKey];
      const i = compositeKey.lastIndexOf(':');
      const aircraftCode = compositeKey.slice(0, i);
      const dataSource = compositeKey.slice(i + 1);
      if (!this.simplifiedPositionId[aircraftCode]) {
        this.simplifiedPositionId[aircraftCode] = {};
      }
      this.simplifiedPositionId[aircraftCode][dataSource] = tmp.simplifiedPositionId;
    });

    this.coPilotDh.retained.data = [...pointFeatures, ...lineStringFeatures];
  }

  _minIntervalSecByZoom() {
    const zoom = WRAP.Geo.getZoom();
    //zoomに応じた、それ未満なら間引く という“判定用の閾値”
    const MIN_POINT_INTERVAL_SEC_BY_ZOOM = [
      { maxZoom: 3,  sec: 300 },// 5分
      { maxZoom: 4,  sec: 240 },// 4分
      { maxZoom: 5,  sec: 180 },// 3分
      { maxZoom: 6,  sec: 120 },// 2分
      { maxZoom: 7,  sec: 50 },// 50秒
      { maxZoom: 8,  sec: 20 },// 20秒
      { maxZoom: 9,  sec: 20 },// 20秒
      { maxZoom: 10, sec: 10 },// 10秒
    ];
    return MIN_POINT_INTERVAL_SEC_BY_ZOOM.find(v => zoom < v.maxZoom)?.sec ?? 0
  }

}

export default CoPilot;
